import React, { useContext, useState } from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import ErrorDisplay from "../common/ErrorDisplay";
import { Problem } from "../api/backendApi";
import { drawingApi } from "../api/drawingApi";
import { DrawingContext } from "./DrawingContext";

interface InvalidComponentEditModalProps {
  name: string;
  otherWindows: string[];
  onClose: () => void;
  onRemove: (name: string) => void;
}

function InvalidComponentEditModal({
  name,
  otherWindows,
  onRemove,
  onClose,
}: InvalidComponentEditModalProps) {
  const drawing = useContext(DrawingContext);
  const [error, setError] = useState<Problem | string>();
  const [actionSelection, setActionSelection] = useState<string>();
  const [otherName, setOtherName] = useState<string>();

  const handleOnClose = () => {
    setError(undefined);
    setActionSelection(undefined);
    setOtherName(undefined);
    onClose();
  };

  const handleSave = async () => {
    if (actionSelection === "1") {
      // remove
      const result = await drawingApi.upsertAttributeWindow(
        drawing.name,
        drawing.type,
        name,
        false,
        [],
      );

      if (!result.ok) {
        setError(result.problem);
        return;
      }

      onRemove(name);
    } else if (actionSelection === "2") {
      if (!otherName) {
        setError("You must choose an attribute window to remove.");
        return;
      }

      const getResult = await drawingApi.getAttributeWindow(
        drawing.name,
        drawing.type,
        name,
        false,
      );

      if (!getResult.ok) {
        setError(getResult.problem);
        return;
      }

      // first, remove the other one
      const removeResult = await drawingApi.upsertAttributeWindow(
        drawing.name,
        drawing.type,
        otherName,
        false,
        [],
      );

      if (!removeResult.ok) {
        setError(removeResult.problem);
        return;
      }

      const result = await drawingApi.upsertAttributeWindow(
        drawing.name,
        drawing.type,
        name,
        false,
        getResult.value.map((aw) => ({ ...aw, name: otherName })),
      );

      if (!result.ok) {
        setError(result.problem);
        return;
      }
      onRemove(name);
    } else {
      setError("You must choose one of the options.");
      return;
    }

    handleOnClose();
  };

  return (
    <Modal
      show={true}
      onHide={() => {
        handleOnClose();
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Fix invalid attribute window</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ErrorDisplay error={error} />
        <p>
          The attribute window '{name}' doesn't have any corresponding component
          in the drawing.
        </p>
        <p>
          In order to fix the problem you have 2 options: remove it or replace
          an existing window.
        </p>
        <Form.Check
          className="mb-3"
          id="invalid-component-action-1"
          name="invalid-component-action"
          type="radio"
          value="1"
          label="Remove component window"
          onChange={(e) =>
            e.target.checked && setActionSelection(e.target.value)
          }
        />
        <Form.Check
          className="mb-3"
          id="invalid-component-action-2"
          name="invalid-component-action"
          type="radio"
          value={2}
          label="Replace the contents of another window"
          onChange={(e) =>
            e.target.checked && setActionSelection(e.target.value)
          }
        />
        <InputGroup className="mb-3">
          <InputGroup.Text>Attribute window to replace: </InputGroup.Text>
          <Form.Select
            defaultValue={otherName}
            disabled={actionSelection !== "2"}
            onChange={(e) => setOtherName(e.target.value)}
          >
            {!otherName && <option>[select]</option>}
            {otherWindows.map((ow) => (
              <option key={ow} value={ow}>
                {ow}
              </option>
            ))}
          </Form.Select>
        </InputGroup>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={() => handleOnClose()}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={actionSelection !== "1" && actionSelection !== "2"}
          onClick={() => handleSave()}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default InvalidComponentEditModal;
